import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card,
    Table,
    DatePicker,
    Input,
    Button,
    Space,
    Row,
    Col,
    Alert,
    Dropdown,
    Typography,
    message,
    Pagination,
    Modal,
    Select,
    InputNumber,
    Tooltip,
    ConfigProvider,
    theme,
    Checkbox
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
    SearchOutlined,
    DownloadOutlined,
    PrinterOutlined,
    FileExcelOutlined,
    BarChartOutlined,
    EditOutlined,
    EyeOutlined,
    MoreOutlined,
    FilterOutlined,
    ClearOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { getNumericFilters, getStringFilters, getDateFilters, formatCellValue, applyAdvancedFilter, redirectToPivotWithData, exportToExcel, printTable } from '../../utils/tableFilters';
import { trIncludes } from '../../utils/trText';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import * as XLSX from 'xlsx';
import FormikForm from '../form/FormikForm';
import '../insaat/KurulumHareketleriPage.css';
import Chatbot from '../giris/Chatbot.jsx';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const iconMap = {
    'EyeOutlined': EyeOutlined,
    'EditOutlined': EditOutlined,
    'MoreOutlined': MoreOutlined,
};

const ActionButtons = ({ record, actionButtons, darkMode }) => {
    if (!actionButtons || actionButtons.length === 0) {
        return null;
    }

    if (actionButtons.length > 2) {
        const menuItems = actionButtons.map(button => {
            const IconComponent = iconMap[button.icon];
            return {
                key: button.key,
                label: button.label,
                icon: IconComponent ? <IconComponent /> : null,
                onClick: () => button.onClick(record)
            };
        });

        return (
            <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                placement="bottomRight"
            >
                <Button
                    type="text"
                    icon={<MoreOutlined />}
                    size="small"
                    className={darkMode ? 'dark-button' : ''}
                    style={{
                        color: darkMode ? 'var(--dark-text)' : undefined
                    }}
                />
            </Dropdown>
        );
    }

    return (
        <Space size="small">
            {actionButtons.map(button => {
                const IconComponent = iconMap[button.icon];
                return (
                    <Button
                        key={button.key}
                        type={button.type}
                        size="small"
                        icon={IconComponent ? <IconComponent /> : null}
                        onClick={() => button.onClick(record)}
                        className={darkMode ? 'dark-button' : ''}
                        style={{
                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                            color: darkMode ? 'var(--dark-text)' : undefined
                        }}
                    >
                        {button.label}
                    </Button>
                );
            })}
        </Space>
    );
};

const TamimlerPage = () => {
    const navigate = useNavigate();
    const { darkMode } = useTheme();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [currentFormId, setCurrentFormId] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [formTableName, setFormTableName] = useState('');
    const [formUniqueField, setFormUniqueField] = useState(null);
    const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailData, setDetailData] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [currentRecordId, setCurrentRecordId] = useState(null);
    const [modalButtonType, setModalButtonType] = useState('main-table');
    const [filters, setFilters] = useState({
        dateRange: null,
        search: '',
        page: 1,
        pageSize: 50,
    });
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
        pageSize: 10
    });
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [columns, setColumns] = useState([]);
    const [showData, setShowData] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [columnSelectDropdownOpen, setColumnSelectDropdownOpen] = useState(false);

    useEffect(() => {
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    const tamimlerData = {
        key: 'tamim',
        label: 'Tamimler',
        sqlKey: 'tamim',
        actionButtons: {
            secondToLastColumn: [
                {
                    key: 'view',
                    label: 'Görüntüle',
                    type: 'default',
                    icon: 'EyeOutlined',
                    onClick: (record) => {
                        const key = Object.keys(record);
                        console.log("keys: ", key);
                        const id = record["Tamim Dosyası"];
                        console.log("id: ", id);
                        if (id) {
                            const MINIO_BASE_URL = 'https://dosya.atmturkiye.com';
                            const BUCKET_NAME = 'atmpoint';
                            const pathSegments = id.split('/');
                            const encodedSegments = pathSegments.map(segment => {
                                return encodeURIComponent(segment);
                            });
                            const encodedPath = encodedSegments.join('/');
                            const fileUrl = `${MINIO_BASE_URL}/${BUCKET_NAME}/${encodedPath}`;
                            console.log("File URL: ", fileUrl);
                            window.open(fileUrl, '_blank');
                        } else {
                            message.error('ID bulunamadı');
                        }
                    }
                }
            ]
        }
    };

    const handleOpenForm = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null) => {
        setModalTitle(title);
        setCurrentFormId(formId);
        setFormTableName(tableName);
        setFormUniqueField(uniqueField);
        setCurrentUniqueValue(uniqueValue);
        setFormModalOpen(true);
    };

    const handleFormSuccess = () => {
        setFormModalOpen(false);
        setCurrentFormId(null);
        setFormTableName('');
        setFormUniqueField('');
        loadData();
    };

    const closeFormModal = () => {
        setFormModalOpen(false);
    };

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let url, response;

            url = `${API_BASE_URL}/list/execute/${tamimlerData.sqlKey}`;
            response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`API Error - Status: ${response.status}, Response:`, errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const allData = await response.json();

            if (!Array.isArray(allData)) {
                console.warn('API response is not an array:', allData);
                setError('Geçersiz veri formatı - API array döndürmüyor');
                return;
            }
            let filteredData = [...allData];

            if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                const filterStartDate = filters.dateRange[0].startOf('day');
                const filterEndDate = filters.dateRange[1].endOf('day');
                
                filteredData = filteredData.filter(record => {
                    const tarih = record['Tarih'] || record['tarih'];
                    let recordDate;
                    const dateValue = String(tarih).trim();

                    if (dateValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                        recordDate = dayjs(dateValue, 'DD.MM.YYYY');
                    } else if (dateValue.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                        recordDate = dayjs(dateValue, 'YYYY.MM.DD');
                    } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        recordDate = dayjs(dateValue, 'YYYY-MM-DD');
                    } else if (dateValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
                        recordDate = dayjs(dateValue, 'DD-MM-YYYY');
                    } else {
                        recordDate = dayjs(tarih);
                    }

                    if (recordDate.isValid()) {
                        const isInRange = recordDate.isBetween(filterStartDate, filterEndDate, null, '[]');
                        return isInRange;
                    }         
                    return false;
                });              
            }

            if (filters.search && filters.search.trim() !== '') {
                const searchTerm = filters.search.trim();
                filteredData = filteredData.filter(record => {
                    return Object.values(record).some(value => {
                        if (value === null || value === undefined) return false;
                        return trIncludes(String(value), searchTerm);
                    });
                });
            }

            if (filteredData.length > 0) {
                const sample = filteredData[0];
                const dynamicCols = Object.keys(sample)
                    .filter((key) => !key.toLowerCase().includes('fatura') && 
                                   !key.toLowerCase().includes('türü') &&
                                   !key.toLowerCase().includes('key_depo') &&
                                   !key.toLowerCase().includes('key_kabin_islem'))
                    .map((key) => {
                        const firstValue = sample[key];
                        const isDateColumn = key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar');
                        const isNumber = typeof firstValue === "number" && !isNaN(firstValue);
                        const uniqueValues = [...new Set(filteredData.map((item) => item[key]))].filter(
                            (val) => val !== undefined && val !== null && val !== ""
                        );

                        let filters = [];
                        if (isNumber) {
                            filters = getNumericFilters();
                        } else if (isDateColumn) {
                            filters = getDateFilters();
                        } else {
                            filters = getStringFilters();
                        }

                        return {
                            key: key,
                            title: key,
                            dataIndex: key,
                            isDateColumn,
                            isNumber,
                            isString: !isNumber && !isDateColumn,
                            uniqueValues,
                            sortable: true,
                            filters, 
                        };
                    });
                setColumns(dynamicCols);
            }

            setData(filteredData || []);
            setShowData(filteredData || []);
            setPagination(prev => ({
                ...prev,
                total: filteredData?.length || 0
            }));

        } catch (err) {
            console.error('Tamimler data load error:', err);
            setError('Veri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [filters.search, filters.dateRange]);

    const handlePivotRedirect = useCallback(() => {
        redirectToPivotWithData({
            data,
            filters,
            tabLabel: tamimlerData.label || 'Tamimler',
            sqlKey: tamimlerData.sqlKey,
            navigate,
            message,
            setLoading,
            apiBaseUrl: API_BASE_URL,
        });
    }, [data, filters, navigate]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
        
        if (key === 'dateRange') {
            setTimeout(() => {
                loadData();
            }, 100);
        }
        
        if (key === 'search') {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
            
            const newTimeout = setTimeout(() => {
                loadData();
            }, 500); 
            
            setSearchTimeout(newTimeout);
        }
    };

    const handleAdvancedFilterChange = (columnKey, valueType, value) => {
        setFilteredInfo((prev) => ({
            ...prev,
            [columnKey]: {
                ...prev[columnKey],
                [valueType]: value,
            },
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearColumnFilter = (columnKey) => {
        setFilteredInfo((prev) => {
            const newFilteredInfo = { ...prev };
            delete newFilteredInfo[columnKey];
            return newFilteredInfo;
        });
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setFilters({
            dateRange: null,
            search: '',
            page: 1,
            pageSize: 50
        });
        setSortedInfo({});
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    }, []);

    const handleSort = (columnKey) => {
        const column = columns.find((col) => col.key === columnKey);
        if (!column) return;

        let newOrder = "asc";
        if (sortedInfo.columnKey === columnKey) {
            if (sortedInfo.order === "asc") {
                newOrder = "desc";
            } else if (sortedInfo.order === "desc") {
                newOrder = null;
            }
        }

        setSortedInfo({
            columnKey: newOrder ? columnKey : null,
            order: newOrder,
        });
    };

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize
        }));
    };

    const filteredAndSortedData = useMemo(() => {
        let result = data.filter((item) => {
            const matchesSearch = Object.values(item).some((val) =>
                trIncludes(String(val), filters.search)
            );

            const matchesFilters = Object.entries(filteredInfo).every(([key, filter]) => {
                const recordValue = item[key];
                const col = columns.find((c) => c.key === key);
                return applyAdvancedFilter(recordValue, filter, col);
            });

            return matchesSearch && matchesFilters;
        });

        if (sortedInfo.columnKey && sortedInfo.order) {
            const column = columns.find((col) => col.key === sortedInfo.columnKey);
            if (column) {
                result.sort((a, b) => {
                    const valA = a[sortedInfo.columnKey];
                    const valB = b[sortedInfo.columnKey];

                    if (column.isDateColumn) {
                        const dateA = valA ? dayjs(valA) : null;
                        const dateB = valB ? dayjs(valB) : null;
                        if (!dateA && !dateB) return 0;
                        if (!dateA) return sortedInfo.order === "asc" ? -1 : 1;
                        if (!dateB) return sortedInfo.order === "asc" ? 1 : -1;
                        const comparison = dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    } else if (column.isNumber) {
                        const numA = Number(valA) || 0;
                        const numB = Number(valB) || 0;
                        return sortedInfo.order === "asc" ? numA - numB : numB - numA;
                    } else {
                        const strA = String(valA || "").toLowerCase();
                        const strB = String(valB || "").toLowerCase();
                        const comparison = strA.localeCompare(strB, 'tr');
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    }
                });
            }
        }

        return result;
    }, [data, filters.search, filteredInfo, sortedInfo, columns]);

    const paginatedData = useMemo(() => {
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        return filteredAndSortedData.slice(startIndex, startIndex + pagination.pageSize);
    }, [filteredAndSortedData, pagination.current, pagination.pageSize]);

    const removeColumn = (columnKey) => {
        setSelectedColumns((prev) => {
            const newSelected = prev.filter((key) => key !== columnKey);
            saveSelectedColumnsToStorage(newSelected);
            return newSelected;
        });
    };

    const getFilterDropdown = (column) => {
        const currentFilter = filteredInfo[column.key] || {};
        const condition = currentFilter.condition || "choose";

        return (
            <div style={{ padding: 16, minWidth: 300 }}>
                <Space direction="vertical" style={{ width: '100%', backgroundColor: darkMode ? 'var(--dark-bg)' : 'white', border: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}`, padding: 12}}>
                    <div>
                        <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                            Filtre Şartı
                        </Text>
                        <Select
                            size="small"
                            value={condition}
                            onChange={(value) => handleAdvancedFilterChange(column.key, "condition", value)}
                            style={{ width: '100%' }}
                        >
                            {column.filters.map((filter) => (
                                <Option key={filter.value} value={filter.value}>
                                    {filter.text}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {/* String değer seçimi */}
                    {column.isString && condition === "select" && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer Seç
                            </Text>
                            <Select
                                size="small"
                                value={currentFilter.value || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: '100%' }}
                                placeholder="Seçiniz"
                            >
                                <Option value="">Seçiniz</Option>
                                {column.uniqueValues.map((val) => (
                                    <Option key={val} value={val}>
                                        {val}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    {/* Sayısal filtreler için input alanları */}
                    {column.isNumber && condition !== "choose" && condition !== "between" && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer
                            </Text>
                            <InputNumber
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}

                    {/* Sayısal arasında filtresi */}
                    {column.isNumber && condition === "between" && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Min Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Min"
                                    value={currentFilter.min || ""}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "min", value)}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Max Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Max"
                                    value={currentFilter.max || ""}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "max", value)}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </>
                    )}

                    {/* String filtreler için input alanları */}
                    {column.isString && !["choose", "empty", "notEmpty", "select"].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer
                            </Text>
                            <Input
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ""}
                                onChange={(e) => handleAdvancedFilterChange(column.key, "value", e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}

                    {/* Tarih filtreler için input alanları */}
                    {column.isDateColumn && ["equals", "before", "after"].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Tarih
                            </Text>
                            <DatePicker
                                size="small"
                                value={currentFilter.value ? dayjs(currentFilter.value, 'DD.MM.YYYY') : null}
                                onChange={(date) => handleAdvancedFilterChange(column.key, "value", date ? date.format('DD.MM.YYYY') : null)}
                                style={{ width: '100%' }}
                                format="DD.MM.YYYY"
                            />
                        </div>
                    )}

                    {/* Tarih arasında filtresi */}
                    {column.isDateColumn && condition === "between" && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Başlangıç Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.startDate ? dayjs(currentFilter.startDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "startDate", date ? date.format('DD.MM.YYYY') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Bitiş Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.endDate ? dayjs(currentFilter.endDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "endDate", date ? date.format('DD.MM.YYYY') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                />
                            </div>
                        </>
                    )}

                    <Button
                        size="small"
                        onClick={() => clearColumnFilter(column.key)}
                        style={{ marginTop: 8, width: '100%' }}
                    >
                        Filtreyi Sıfırla
                    </Button>
                </Space>
            </div>
        );
    };

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const saveSelectedColumnsToStorage = useCallback((columnKeys) => {
        try {
            const storageKey = `tamimler_selected_columns`;
            localStorage.setItem(storageKey, JSON.stringify(columnKeys));
        } catch (error) {
            console.error('Error saving selected columns to storage:', error);
        }
    }, []);

    useEffect(() => {
        if (columns.length > 0) {
            try {
                const storageKey = `tamimler_selected_columns`;
                const saved = localStorage.getItem(storageKey);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const validColumns = parsed.filter(key => columns.some(col => col.key === key));
                    if (validColumns.length > 0) {
                        setSelectedColumns(validColumns);
                    } else {
                        const allColumnKeys = columns.map(col => col.key);
                        setSelectedColumns(allColumnKeys);
                        saveSelectedColumnsToStorage(allColumnKeys);
                    }
                } else {
                    const allColumnKeys = columns.map(col => col.key);
                    setSelectedColumns(allColumnKeys);
                    saveSelectedColumnsToStorage(allColumnKeys);
                }
            } catch (error) {
                console.error('Error loading selected columns from storage:', error);
                const allColumnKeys = columns.map(col => col.key);
                setSelectedColumns(allColumnKeys);
            }
        }
    }, [columns, saveSelectedColumnsToStorage]);

    const handleColumnSelectionChange = (checkedValues) => {
        setSelectedColumns(checkedValues);
        saveSelectedColumnsToStorage(checkedValues);
    };

    const handleSelectAllColumns = (checked) => {
        if (checked) {
            const allColumnKeys = columns.map(col => col.key);
            setSelectedColumns(allColumnKeys);
            saveSelectedColumnsToStorage(allColumnKeys);
        } else {
            setSelectedColumns([]);
            saveSelectedColumnsToStorage([]);
        }
    };

    const visibleColumns = useMemo(() => {
        if (selectedColumns.length === 0) {
            return columns;
        }
        return columns.filter(col => selectedColumns.includes(col.key));
    }, [columns, selectedColumns]);

    return (
        <div className={`kurulum-page ${darkMode ? 'dark-theme' : ''}`}>
            {/* Filtreler */}
            <Card
                className={`kurulum-filters-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                {/* Başlık - En üstte */}
                <Row style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Title level={3} style={{ 
                            color: darkMode ? 'var(--dark-text)' : undefined,
                            margin: 0,
                            textAlign: 'center'
                        }}>
                            {tamimlerData.label}
                            <EditOutlined onClick={() => handleOpenForm(275, 'Liste Formu', 'sqls', 'id', 137)} />
                        </Title>
                    </Col>
                </Row>

                {/* Filtreler */}
                <Row gutter={[16, 16]} align="middle">
                    {/* Tarih Aralığı - En solda */}
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Tarih Aralığı
                            </Text>
                            <RangePicker
                                value={filters.dateRange}
                                onChange={(dates) => handleFilterChange('dateRange', dates)}
                                style={{
                                    width: '100%',
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                placeholder={['Başlangıç', 'Bitiş']}
                                format="DD.MM.YYYY"
                                className={darkMode ? 'dark-date-picker' : ''}
                                locale={{
                                    lang: {
                                        locale: 'tr_TR',
                                        placeholder: 'Tarih seçin',
                                        rangePlaceholder: ['Başlangıç', 'Bitiş'],
                                        today: 'Bugün',
                                        now: 'Şimdi',
                                        backToToday: 'Bugüne dön',
                                        ok: 'Tamam',
                                        clear: 'Temizle',
                                        month: 'Ay',
                                        year: 'Yıl',
                                        timeSelect: 'Zaman seç',
                                        dateSelect: 'Tarih seç',
                                        monthSelect: 'Ay seç',
                                        yearSelect: 'Yıl seç',
                                        decadeSelect: 'On yıl seç',
                                        yearFormat: 'YYYY',
                                        dateFormat: 'DD.MM.YYYY',
                                        dayFormat: 'DD',
                                        dateTimeFormat: 'DD.MM.YYYY HH:mm:ss',
                                        monthFormat: 'MMMM',
                                        monthBeforeYear: true,
                                        previousMonth: 'Önceki ay (PageUp)',
                                        nextMonth: 'Sonraki ay (PageDown)',
                                        previousYear: 'Önceki yıl (Control + left)',
                                        nextYear: 'Sonraki yıl (Control + right)',
                                        previousDecade: 'Önceki on yıl',
                                        nextDecade: 'Sonraki on yıl',
                                        previousCentury: 'Önceki yüzyıl',
                                        nextCentury: 'Sonraki yüzyıl',
                                    },
                                    timePickerLocale: {
                                        placeholder: 'Zaman seçin',
                                    },
                                }}
                            />
                        </div>
                    </Col>

                    {/* Arama - Tarih yanında */}
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Arama
                            </Text>
                            <Input
                                placeholder="Arama yapın..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                prefix={<SearchOutlined style={{ color: darkMode ? 'var(--dark-text-muted)' : undefined }} />}
                                allowClear
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    </Col>

                    {/* Aksiyon Butonları - Sağ tarafta */}
                    <Col xs={24} sm={24} md={12} lg={16}>
                        <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Space wrap>
                                <Button
                                    onClick={clearAllFilters}
                                    icon={<ClearOutlined />}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    Temizle
                                </Button>
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'excel',
                                                icon: <FileExcelOutlined />,
                                                label: 'Excel (.xlsx)',
                                                onClick: () => exportToExcel(filteredAndSortedData)
                                            },
                                            {
                                                key: 'print',
                                                icon: <PrinterOutlined />,
                                                label: 'Yazdır',
                                                onClick: () => printTable(filteredAndSortedData, { title: tamimlerData?.label || 'Tamimler', message })
                                            }
                                        ]
                                    }}
                                    trigger={['click']}
                                >
                                    <Button
                                        icon={<DownloadOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Dışa Aktar
                                    </Button>
                                </Dropdown>
                                <Dropdown
                                    open={columnSelectDropdownOpen}
                                    onOpenChange={setColumnSelectDropdownOpen}
                                    trigger={['click']}
                                    dropdownRender={() => (
                                        <div style={{
                                            padding: '2px',
                                            minWidth: '250px',
                                            maxHeight: '400px',
                                            overflowY: 'auto',
                                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : '#fff',
                                            border: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}`,
                                            borderRadius: '6px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}>
                                            <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}` }}>
                                                <Checkbox
                                                    checked={selectedColumns.length === columns.length && columns.length > 0}
                                                    indeterminate={selectedColumns.length > 0 && selectedColumns.length < columns.length}
                                                    onChange={(e) => handleSelectAllColumns(e.target.checked)}
                                                    style={{ color: darkMode ? 'var(--dark-text)' : undefined }}
                                                >
                                                    <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                        Tümünü Seç
                                                    </Text>
                                                </Checkbox>
                                            </div>
                                            <Checkbox.Group
                                                value={selectedColumns}
                                                onChange={handleColumnSelectionChange}
                                                style={{ width: '100%' }}
                                            >
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    {columns.map(column => (
                                                        <Checkbox
                                                            key={column.key}
                                                            value={column.key}
                                                            style={{
                                                                width: '100%',
                                                                color: darkMode ? 'var(--dark-text)' : undefined
                                                            }}
                                                        >
                                                            {column.title}
                                                        </Checkbox>
                                                    ))}
                                                </Space>
                                            </Checkbox.Group>
                                        </div>
                                    )}
                                >
                                    <Button
                                        icon={<EyeOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Sütun Seç
                                    </Button>
                                </Dropdown>
                                {/* Chatbot */}
                                <Chatbot />
                            </Space>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Tablo */}
            <Card
                className={`kurulum-content-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <div>
                    {/* Aksiyon Butonları */}
                    <div className="kurulum-actions" style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Space>
                            <Button
                                icon={<BarChartOutlined />}
                                onClick={handlePivotRedirect}
                                disabled={loading || data.length === 0}
                                className={darkMode ? 'dark-button' : ''}
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                            >
                                Pivot Tabloya Git
                            </Button>
                        </Space>
                    </div>

                    {/* Tablo */}
                    {error && (
                        <Alert
                            message="Hata"
                            description={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Table
                        dataSource={paginatedData}
                        loading={loading}
                        rowKey={(record) => record['Platform No'] || Math.random()}
                        scroll={{ x: 'max-content' }}
                        className={darkMode ? 'dark-table' : ''}
                        pagination={{
                            current: pagination.current,
                            total: filteredAndSortedData.length,
                            pageSize: pagination.pageSize,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `Toplam ${total} kayıt (${range[0]}-${range[1]})`,
                            onChange: handlePageChange,
                            onShowSizeChange: handlePageChange,
                            pageSizeOptions: ['10', '25', '50', '100']
                        }}
                        size="small"
                        style={{
                            width: '100%',
                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                        }}
                        columns={visibleColumns.map((column, index) => {
                            const getColumnWidth = (key) => {
                                const keyLength = key.length;

                                if (key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar')) {
                                    return Math.max(120, keyLength * 8);
                                }
                                if (key.toLowerCase().includes('tutar') || key.toLowerCase().includes('fiyat') || key.toLowerCase().includes('ücret')) {
                                    return Math.max(120, keyLength * 8);
                                }
                                if (keyLength > 20) {
                                    return Math.max(200, keyLength * 8);
                                }
                                if (keyLength > 10) {
                                    return Math.max(120, keyLength * 8);
                                }
                                return Math.max(80, keyLength * 8);
                            };

                            const totalColumns = columns.length;
                            const isLastColumn = index === totalColumns - 1;
                            const isSecondToLastColumn = index === totalColumns - 2;
                            const hasActionButtons = tamimlerData && tamimlerData.actionButtons;
                            const hasSecondToLastButtons = hasActionButtons && tamimlerData.actionButtons.secondToLastColumn && tamimlerData.actionButtons.secondToLastColumn.length > 0;
                            const hasLastButtons = hasActionButtons && tamimlerData.actionButtons.lastColumn && tamimlerData.actionButtons.lastColumn.length > 0;

                            const shouldShowActionButtons = (isSecondToLastColumn && hasSecondToLastButtons) || (isLastColumn && hasLastButtons);

                            let actionButtonsToShow = [];
                            if (isSecondToLastColumn) {
                                actionButtonsToShow = tamimlerData.actionButtons?.secondToLastColumn || [];
                            } else if (isLastColumn) {
                                actionButtonsToShow = tamimlerData.actionButtons?.lastColumn || [];
                            }

                            return {
                                title: (
                                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <span style={{ flex: 1, cursor: 'pointer' }}>{column.title}</span>
                                        <Dropdown
                                            overlay={getFilterDropdown(column)}
                                            trigger={['click']}
                                            placement="bottomRight"
                                        >
                                            <Button 
                                                type="text" 
                                                size="small" 
                                                style={{ 
                                                    marginLeft: 4,
                                                    color: filteredInfo[column.key] ? (darkMode ? 'var(--dark-text)' : '#1890ff') : '#ccc'
                                                }}
                                            >
                                                <FilterOutlined style={{ fontSize: 12 }} />
                                            </Button>
                                        </Dropdown>
                                    </div>
                                ),
                                onHeaderCell: () => ({
                                    onContextMenu: (e) => {
                                        e.preventDefault();
                                        removeColumn(column.key);
                                    }
                                }),
                                dataIndex: column.key,
                                key: column.key,
                                align: 'center',
                                width: shouldShowActionButtons ? Math.max(getColumnWidth(column.key), 200) : getColumnWidth(column.key),
                                ellipsis: {
                                    showTitle: true
                                },
                                render: (text, record) => (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {shouldShowActionButtons ? (
                                            <ActionButtons
                                                record={record}
                                                actionButtons={actionButtonsToShow}
                                                darkMode={darkMode}
                                            />
                                        ) : (
                                            <span>{formatCellValue(column.key, text)}</span>
                                        )}
                                    </div>
                                ),
                                sorter: column.sortable ? (a, b) => {
                                    const aVal = a[column.key];
                                    const bVal = b[column.key];
                                    if (column.isNumber) {
                                        const numA = Number(aVal) || 0;
                                        const numB = Number(bVal) || 0;
                                        return numA - numB;
                                    } else if (column.isDateColumn) {
                                        const dateA = aVal ? dayjs(aVal) : null;
                                        const dateB = bVal ? dayjs(bVal) : null;
                                        if (!dateA && !dateB) return 0;
                                        if (!dateA) return -1;
                                        if (!dateB) return 1;
                                        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                                    } else {
                                        return String(aVal || "").localeCompare(String(bVal || ""), 'tr');
                                    }
                                } : false,
                                showSorterTooltip: false
                            };
                        })}
                    />
                </div>
            </Card>

            {/* Form Modal */}
            {formModalOpen && (
                <Modal
                    title={modalTitle}
                    open={formModalOpen}
                    onCancel={closeFormModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={800}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        <FormikForm
                            formId={currentFormId}
                            uniqueValue={currentUniqueValue}
                            uniqueField={formUniqueField}
                            tableName={formTableName}
                            onSuccess={handleFormSuccess}
                        />
                    </div>
                </Modal>
            )}

            {/* Detail Modal */}
            {detailModalOpen && (
                <Modal
                    title={modalTitle}
                    open={detailModalOpen}
                    onCancel={closeDetailModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={1200}
                    style={{ top: 20 }}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        {detailError && (
                            <Alert
                                message="Hata"
                                description={detailError}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <Table
                            dataSource={detailData}
                            loading={detailLoading}
                            rowKey={(record) => record.id || record.key_oys || Math.random()}
                            scroll={{ x: 'max-content' }}
                            className={darkMode ? 'dark-table' : ''}
                            size="small"
                            style={{
                                width: '100%',
                                backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                            }}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} / ${total} kayıt`,
                                pageSizeOptions: ['10', '25', '50', '100']
                            }}
                        >
                            {detailData && detailData.length > 0 && Object.keys(detailData[0]).map((key, index) => {
                                const getColumnWidth = (key) => {
                                    const keyLength = key.length;

                                    if (key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar')) {
                                        return Math.max(120, keyLength * 8);
                                    }
                                    if (key.toLowerCase().includes('tutar') || key.toLowerCase().includes('fiyat') || key.toLowerCase().includes('ücret')) {
                                        return Math.max(120, keyLength * 8);
                                    }
                                    if (keyLength > 20) {
                                        return Math.max(200, keyLength * 8);
                                    }
                                    if (keyLength > 10) {
                                        return Math.max(120, keyLength * 8);
                                    }
                                    return Math.max(80, keyLength * 8);
                                };

                                return (
                                    <Table.Column
                                        key={key}
                                        title={key}
                                        dataIndex={key}
                                        width={getColumnWidth(key)}
                                        ellipsis={{
                                            showTitle: true
                                        }}
                                        render={(text, record) => (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <span>{formatCellValue(key, text)}</span>
                                            </div>
                                        )}
                                        sorter={(a, b) => {
                                            const aVal = a[key];
                                            const bVal = b[key];
                                            if (!isNaN(aVal) && !isNaN(bVal)) {
                                                return Number(aVal) - Number(bVal);
                                            }
                                            return String(aVal).localeCompare(String(bVal), 'tr');
                                        }}
                                    />
                                );
                            })}
                        </Table>
                    </div>
                </Modal>
            )}

        </div>
    );
};

export default TamimlerPage;